import { Suspense, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { PageNav } from "./PageNav";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Header } from "./components";
import { environment } from "./environment";
import { hideStartupOverlay } from "./utils/startupOverlay";
import { X } from "lucide-react";

function App() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      hideStartupOverlay();
    }, 5000);

    return () => window.clearTimeout(timeoutId);
  }, []);

  return (
    <ThemeProvider defaultTheme="light">
      <div className="h-screen flex flex-col bg-background">
        <Header onToggleMobileMenu={toggleMobileMenu} />

        <div className="flex-1 relative bg-background min-h-0">
          <aside className="hidden md:block fixed left-0 top-16 bottom-0 z-20">
            <div
              className="w-64 h-full"
              style={{
                marginLeft: `max(0px, calc((100vw - 1280px) / 2 + 1rem))`,
              }}
            >
              <div className="h-full p-4">
                <PageNav />
              </div>
            </div>
          </aside>

          <main className="absolute inset-0 overflow-y-auto md:ml-64">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 md:pb-12">
              <Suspense
                fallback={null}
              >
                <Outlet />
              </Suspense>
            </div>
          </main>
        </div>

        <>
          <div
            className={`fixed inset-0 bg-black z-40 md:hidden transition-opacity duration-300 ease-in-out ${
              isMobileMenuOpen
                ? "bg-opacity-50"
                : "bg-opacity-0 pointer-events-none"
            }`}
            onClick={closeMobileMenu}
          />
          <div
            className={`fixed inset-y-0 left-0 z-50 w-64 bg-surface shadow-lg md:hidden transform transition-transform duration-300 ease-in-out ${
              isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <div className="h-full flex flex-col">
              <div className="flex-shrink-0 p-4">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <img
                      src={`${environment.resourceUrl}/authhub-mark.svg`}
                      alt="AuthHub"
                      className="h-8 w-8 object-contain"
                    />
                    <h2 className="text-lg font-semibold text-text-primary">
                      AuthHub
                    </h2>
                  </div>
                  <button
                    onClick={closeMobileMenu}
                    className="p-2 rounded-md text-text-tertiary hover:text-text-secondary hover:bg-hover-medium"
                    aria-label="Close navigation menu"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                <div className="border-b border-border-primary"></div>
              </div>
              <div className="flex-1 p-4 pt-0 overflow-y-auto min-h-0">
                <PageNav onCloseMobileMenu={closeMobileMenu} />
              </div>
            </div>
          </div>
        </>

        <Toaster
          position="top-center"
          containerStyle={{
            top: "5rem",
          }}
          toastOptions={{
            duration: 4000,
            style: {
              background: "rgb(var(--color-surface))",
              color: "rgb(var(--color-text-secondary))",
              border: "1px solid rgb(var(--color-border-primary))",
              borderRadius: "0.75rem",
              maxWidth: "min(90vw, 720px)",
              boxShadow:
                "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
            },
            success: {
              iconTheme: {
                primary: "rgb(var(--color-success-600))",
                secondary: "rgb(var(--color-text-inverse))",
              },
            },
            error: {
              iconTheme: {
                primary: "rgb(var(--color-error-600))",
                secondary: "rgb(var(--color-text-inverse))",
              },
            },
          }}
        />
      </div>
    </ThemeProvider>
  );
}

export default App;
